// ToastNotifier.js
class ToastNotifier {
    constructor({
        domPurify = null,
        translator,
        containerId = 'app-toast-container',
        containerClass = 'toast-container position-fixed top-0 end-0 p-3 d-flex flex-column gap-2',
        containerZIndex = 1090,
        autoDismissMs = 4000,
    } = {}) {
        if (!translator) throw new Error('ToastNotifier: translator is required');

        this.domPurify = domPurify;
        this.translator = translator;
        this.containerId = containerId;
        this.containerClass = containerClass;
        this.containerZIndex = containerZIndex;
        this.autoDismissMs = autoDismissMs;

        this._purifyConfig = Object.freeze({
            ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'a', 'span', 'small', 'code', 'p'],
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
        });

        if (this.domPurify && !this._toastNotifierHookInstalled) {
            this.domPurify.addHook('afterSanitizeAttributes', (node) => {
                if (node.tagName === 'A') {
                    node.setAttribute('rel', 'noopener noreferrer');
                    // node.setAttribute('target', '_blank');
                }
            });
            this._toastNotifierHookInstalled = true;
        }
    }

    sanitizeHtml(html) {
        if (!this.domPurify) return String(html || '');
        return this.domPurify.sanitize(String(html || ''), this._purifyConfig);
    }

    ensureContainer() {
        let container = document.getElementById(this.containerId);
        if (!container) {
            container = document.createElement('div');
            container.id = this.containerId;
            container.className = this.containerClass;
            container.style.zIndex = String(this.containerZIndex);
            container.style.pointerEvents = 'none';
            document.body.appendChild(container);
        }
        return container;
    }

    getTitle(type) {
        const normalized = String(type || 'info').toLowerCase();
        if (normalized === 'danger' || normalized === 'error') return this.translator.trans('common.error');
        if (normalized === 'warning') return this.translator.trans('common.warning');
        if (normalized === 'success') return this.translator.trans('common.success');
        return this.translator.trans('common.info');
    }

    getBgClass(type) {
        const normalized = String(type || 'info').toLowerCase();
        if (normalized === 'success') return 'text-bg-success';
        if (normalized === 'danger' || normalized === 'error') return 'text-bg-danger';
        if (normalized === 'warning') return 'text-bg-warning';
        if (normalized === 'primary') return 'text-bg-primary';
        if (normalized === 'secondary') return 'text-bg-secondary';
        if (normalized === 'dark') return 'text-bg-dark';
        if (normalized === 'light') return 'text-bg-light';
        return 'text-bg-info';
    }

    buildToast({ type = 'info', title = null, message = '', allowHtml = false, dismissMs = null }) {
        const bgClass = this.getBgClass(type);
        const resolvedTitle = title || this.getTitle(type);
        const resolvedDismissMs = typeof dismissMs === 'number' ? dismissMs : this.autoDismissMs;

        const wrap = document.createElement('div');
        wrap.style.pointerEvents = 'auto';

        const toast = document.createElement('div');
        toast.className = 'toast align-items-center border-0 shadow-sm rounded-3 overflow-hidden bg-transparent';
        const urgent = ['danger', 'error', 'warning'].includes(type);
        toast.setAttribute('role', urgent ? 'alert' : 'status');
        toast.setAttribute('aria-live', urgent ? 'assertive' : 'polite');
        toast.setAttribute('aria-atomic', 'true');
        toast.setAttribute('data-bs-delay', String(resolvedDismissMs));

        const flex = document.createElement('div');
        flex.className = `d-flex align-items-center w-100 ${bgClass}`;

        const body = document.createElement('div');
        body.className = 'toast-body py-3 px-3 flex-grow-1';

        const titleEl = document.createElement('div');
        titleEl.className = 'fw-semibold';
        titleEl.textContent = resolvedTitle;

        const msgEl = document.createElement('div');
        if (allowHtml) {
            msgEl.innerHTML = this.sanitizeHtml(message);
        } else {
            msgEl.textContent = String(message || '');
        }

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'btn-close btn-close-white me-3';
        closeBtn.setAttribute('data-bs-dismiss', 'toast');
        closeBtn.setAttribute('aria-label', this.translator.trans('common.close'));

        body.appendChild(titleEl);
        body.appendChild(msgEl);

        flex.appendChild(body);
        flex.appendChild(closeBtn);
        toast.appendChild(flex);
        wrap.appendChild(toast);

        return { wrap, toast, dismissMs: resolvedDismissMs };
    }

    setupAutoDismiss(node, closeFn, ms) {
        if (ms <= 0) return;

        let remaining = ms;
        let start = Date.now();
        let timer = setTimeout(closeFn, remaining);

        node.addEventListener('mouseenter', () => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
                remaining -= (Date.now() - start);
            }
        });

        node.addEventListener('mouseleave', () => {
            if (remaining > 0) {
                start = Date.now();
                timer = setTimeout(closeFn, remaining);
            }
        });
    }

    show({
        type = 'info',
        title = null,
        message = '',
        allowHtml = false,
        dismissMs = null,
    } = {}) {
        const container = this.ensureContainer();
        const built = this.buildToast({ type, title, message, allowHtml, dismissMs });

        container.prepend(built.wrap);

        try {
            if (window.bootstrap && bootstrap.Toast) {
                bootstrap.Toast.getOrCreateInstance(built.toast, { autohide: false }).show();
            } else {
                built.toast.classList.add('show');
            }
        } catch (_) {
            built.toast.classList.add('show');
        }

        const closeToast = () => {
            try {
                if (window.bootstrap && bootstrap.Toast) {
                    const instance = bootstrap.Toast.getOrCreateInstance(built.toast, { autohide: false });
                    instance.hide();
                    setTimeout(() => built.wrap.remove(), 350);
                } else {
                    built.toast.classList.remove('show');
                    setTimeout(() => built.wrap.remove(), 200);
                }
            } catch (_) {
                built.wrap.remove();
            }
        };

        this.setupAutoDismiss(built.wrap, closeToast, built.dismissMs);

        return built.toast;
    }

    success(message, options = {}) {
        return this.show({ ...options, type: 'success', message });
    }

    error(message, options = {}) {
        return this.show({ ...options, type: 'danger', message });
    }

    warning(message, options = {}) {
        return this.show({ ...options, type: 'warning', message });
    }

    info(message, options = {}) {
        return this.show({ ...options, type: 'info', message });
    }
}

export default ToastNotifier;